import { FAQS } from './faqs.js';
import { AREAS } from './areas.js';

export const SITE_URL = 'https://proclimbsolutions.com.au';
export const SITE_NAME = 'ProClimb Solutions';
export const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const pages = {
  '/': {
    title: 'Rope Access Services Sydney & NSW | ProClimb Solutions',
    description:
      'ProClimb Solutions provides professional rope access services in Sydney and across NSW: high-rise window and facade cleaning, remedial work, glazing, height safety and 24/7 emergency response.',
  },
  '/services': {
    title: 'Rope Access Services | ProClimb Solutions',
    description:
      'High-rise window and facade cleaning, remedial work, glazing, height safety systems, bird control and emergency rope access across Sydney and NSW.',
  },
  '/services/window-cleaning': {
    title: 'High Rise Window & Facade Cleaning Sydney | ProClimb Solutions',
    description:
      'Safe, efficient rope access window and facade cleaning for high-rise buildings in Sydney and NSW, delivered by certified technicians.',
  },
  '/services/facade-cleaning': {
    title: 'Facade Cleaning Sydney | ProClimb Solutions',
    description:
      'Rope access facade cleaning for high-rise and complex buildings in Sydney and NSW, delivered by certified technicians.',
  },
  '/services/remedial': {
    title: 'Remedial Work & Building Maintenance at Height | ProClimb Solutions',
    description:
      'Rope access remedial work and building maintenance at height across Sydney and NSW, without the cost and disruption of scaffolding.',
  },
  '/services/glazing': {
    title: 'Glazing & Panel Installation at Height | ProClimb Solutions',
    description:
      'Rope access glazing and panel installation and replacement on high-rise and complex structures in Sydney and NSW.',
  },
  '/services/height-safety': {
    title: 'Height Safety Systems Installation & Inspection | ProClimb Solutions',
    description:
      'Installation, inspection and certification of height safety systems, anchor points and lifelines for buildings in Sydney and NSW.',
  },
  '/services/bird-control': {
    title: 'Bird Pest Control & Facade Protection | ProClimb Solutions',
    description:
      'Bird deterrent installation and facade protection on high-rise and hard-to-reach areas in Sydney and NSW using rope access.',
  },
  '/services/emergency': {
    title: 'Emergency Rope Access & Rapid Response | ProClimb Solutions',
    description:
      'Fast-response emergency rope access for urgent building repairs, make-safe works and facade issues across Sydney and NSW. Call 0426 920 301.',
  },
  '/about': {
    title: 'About ProClimb Solutions | Rope Access Specialists',
    description:
      'Meet ProClimb Solutions: certified rope access specialists with over 10 years of experience delivering safe, high-quality work at height across NSW.',
  },
  '/projects': {
    title: 'Projects & Gallery | ProClimb Solutions',
    description:
      'See ProClimb Solutions rope access projects across NSW, including facade cleaning, remedial works, glazing and height safety installations.',
  },
  '/premium': {
    title: 'Luxury Home Exterior Services | ProClimb Solutions',
    description:
      'Rope access cleaning and maintenance for high-value private residences and architecturally unique homes in Sydney and NSW.',
  },
  '/contact': {
    title: 'Contact & Request a Quote | ProClimb Solutions',
    description:
      'Request a free quote from ProClimb Solutions. Call 0426 920 301 or email info@proclimbsolutions.com.au for rope access services in Sydney and NSW.',
  },
  '/safety-compliance': {
    title: 'Safety & Compliance | ProClimb Solutions',
    description:
      'How ProClimb Solutions keeps every rope access job safe and compliant with Australian standards, certifications and work-at-height procedures.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | ProClimb Solutions',
    description: 'How ProClimb Solutions collects, uses and protects your personal information.',
  },
  '/terms-conditions': {
    title: 'Terms & Conditions | ProClimb Solutions',
    description: 'Terms and conditions for ProClimb Solutions rope access services and website use.',
  },
  '/disclaimer': {
    title: 'Disclaimer | ProClimb Solutions',
    description: 'Website disclaimer for ProClimb Solutions.',
  },
};

pages['/areas'] = {
  title: 'Areas We Service | Rope Access Sydney & NSW | ProClimb Solutions',
  description:
    'ProClimb Solutions provides rope access services across Sydney, Newcastle, Wollongong and surrounding NSW regions. Find your area and request a quote.',
};
AREAS.forEach((a) => {
  pages[`/areas/${a.slug}`] = { title: a.metaTitle, description: a.metaDescription };
});

export const getPageMeta = (pathname) => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const exact = pages[path];
  if (exact) return { ...exact, path };
  if (path.startsWith('/services/')) return { ...pages['/services'], path: '/services' };
  if (path.startsWith('/areas/')) return { ...pages['/areas'], path: '/areas' };
  return { ...pages['/'], path: '/' };
};

export const canonicalUrl = (path) => `${SITE_URL}${path === '/' ? '/' : `${path}/`}`;

export const SITEMAP_PATHS = Object.keys(pages);

export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#business`,
  name: SITE_NAME,
  url: SITE_URL,
  image: DEFAULT_IMAGE,
  logo: `${SITE_URL}/proclimb-logo-trimmed.png`,
  telephone: '+61426920301',
  email: 'info@proclimbsolutions.com.au',
  description:
    'Professional rope access services in Sydney and across NSW: high-rise window and facade cleaning, remedial work, glazing, height safety and emergency response. IRATA Level 3 certified technicians with 10+ years of experience.',
  areaServed: { '@type': 'AdministrativeArea', name: 'New South Wales, Australia' },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '21 Mascot Drive',
    addressLocality: 'Eastlakes',
    addressRegion: 'NSW',
    postalCode: '2018',
    addressCountry: 'AU',
  },
  knowsAbout: [
    'Rope access',
    'High-rise window cleaning',
    'Facade cleaning',
    'Remedial building work',
    'Glazing and panel installation',
    'Height safety systems',
    'Bird control',
    'Emergency rope access',
  ],
  hasCredential: { '@type': 'EducationalOccupationalCredential', name: 'IRATA Level 3 certified technicians' },
};

const SERVICE_NAMES = {
  '/services/window-cleaning': 'High Rise Window & Facade Cleaning',
  '/services/facade-cleaning': 'Facade Cleaning',
  '/services/remedial': 'Remedial Work & Building Maintenance at Height',
  '/services/glazing': 'Glazing & Panel Installation at Height',
  '/services/height-safety': 'Height Safety Systems Installation & Inspection',
  '/services/bird-control': 'Bird Pest Control & Facade Protection',
  '/services/emergency': 'Emergency Rope Access & Rapid Response',
};

export const getSchemas = (path) => {
  if (path === '/') {
    return [
      localBusinessSchema,
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ];
  }
  const schemas = [];
  const crumbs = [{ name: 'Home', url: canonicalUrl('/') }];
  if (path.startsWith('/services/')) crumbs.push({ name: 'Services', url: canonicalUrl('/services') });
  if (path.startsWith('/areas/')) crumbs.push({ name: 'Areas', url: canonicalUrl('/areas') });
  crumbs.push({ name: getPageMeta(path).title.split(' | ')[0], url: canonicalUrl(path) });
  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, item: c.url })),
  });
  if (SERVICE_NAMES[path]) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: SERVICE_NAMES[path],
      description: getPageMeta(path).description,
      url: canonicalUrl(path),
      provider: { '@id': `${SITE_URL}/#business` },
      areaServed: { '@type': 'AdministrativeArea', name: 'New South Wales, Australia' },
    });
  }
  if (path.startsWith('/areas/')) {
    const area = AREAS.find((a) => `/areas/${a.slug}` === path);
    if (area) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: `Rope access services in ${area.name}`,
        description: getPageMeta(path).description,
        url: canonicalUrl(path),
        provider: { '@id': `${SITE_URL}/#business` },
        areaServed: area.suburbs.map((n) => ({ '@type': 'Place', name: n })),
      });
    }
  }
  return schemas;
};
